import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Statistic, Typography, Card, Space, Spin } from "antd";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import { fetchFormAnalytics } from "../../../redux/actions/formsAction";
import { useFormDetail } from "./FormDetailContext";
import {
  buildSubmissionsLineData,
  buildStatusBarData,
  CHART_COLORS,
  hasChartActivity,
} from "./formChartUtils";

const { Text } = Typography;

function ChartSectionHeader({ title, period }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
      }}
    >
      <Text
        type="secondary"
        style={{
          fontSize: 11,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {title}
      </Text>
      {period && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {period}
        </Text>
      )}
    </div>
  );
}

const STATUS_BAR_KEYS = ["Confirmed", "Pending", "Spam", "Rejected"];

export default function FormOverview() {
  const { id } = useFormDetail();
  const dispatch = useDispatch();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchFormAnalytics(id))
      .then((data) => {
        setAnalytics(data);
        setLoading(false);
      })
      .catch(() => {
        setAnalytics(null);
        setLoading(false);
      });
  }, [dispatch, id]);

  const lineData = useMemo(() => {
    const timeline =
      analytics?.submissionsTimeline ||
      analytics?.submissionsPerDay?.map((r) => ({
        date: r._id,
        inbox: r.count,
        spam: 0,
      }));
    return buildSubmissionsLineData(timeline);
  }, [analytics]);

  const statusData = useMemo(
    () => buildStatusBarData(analytics?.statusPerDay),
    [analytics],
  );

  const hasTimelineActivity = hasChartActivity(lineData, ["inbox", "spam"]);
  const hasStatusActivity = hasChartActivity(statusData, STATUS_BAR_KEYS);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!analytics) {
    return (
      <Text type="secondary">
        Analytics will appear after your form receives submissions.
      </Text>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Total submissions" value={analytics.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Promoted to contacts" value={analytics.promoted} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Marked as spam" value={analytics.spam} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Spam rate"
              value={Math.round((analytics.spamRate || 0) * 100)}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" styles={{ body: { padding: "16px 20px 8px" } }}>
        <ChartSectionHeader title="Submissions" period="Last 30 days" />
        {hasTimelineActivity ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={lineData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E8ECEF" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6B7A82" }}
                tickLine={false}
                axisLine={{ stroke: "#D8E0E3" }}
                interval="preserveStartEnd"
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#6B7A82" }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              <Tooltip
                labelFormatter={(_, payload) =>
                  payload?.[0]?.payload?.date
                    ? new Date(payload[0].payload.date + "T12:00:00Z").toLocaleDateString(
                        undefined,
                        { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" },
                      )
                    : ""
                }
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="inbox"
                name="Inbox"
                stroke={CHART_COLORS.inbox}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="spam"
                name="Spam"
                stroke={CHART_COLORS.spam}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text type="secondary">No submissions in the last 30 days.</Text>
          </div>
        )}
        <Text
          type="secondary"
          style={{
            display: "block",
            textAlign: "center",
            fontSize: 11,
            marginTop: 4,
          }}
        >
          Timezone: UTC
        </Text>
      </Card>

      <Card size="small" styles={{ body: { padding: "16px 20px 8px" } }}>
        <ChartSectionHeader title="Submissions by status" period="Last 30 days" />
        {hasStatusActivity ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E8ECEF" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6B7A82" }}
                tickLine={false}
                axisLine={{ stroke: "#D8E0E3" }}
                interval="preserveStartEnd"
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#6B7A82" }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              <Tooltip />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: 12 }}
              />
              <Bar
                stackId="status"
                dataKey="Confirmed"
                fill={CHART_COLORS.confirmed}
                radius={[0, 0, 0, 0]}
              />
              <Bar stackId="status" dataKey="Pending" fill={CHART_COLORS.pending_confirm} />
              <Bar stackId="status" dataKey="Spam" fill={CHART_COLORS.spamStatus} />
              <Bar
                stackId="status"
                dataKey="Rejected"
                fill={CHART_COLORS.rejected}
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              height: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text type="secondary">No submissions in the last 30 days.</Text>
          </div>
        )}
        <Text
          type="secondary"
          style={{
            display: "block",
            textAlign: "center",
            fontSize: 11,
            marginTop: 4,
          }}
        >
          Timezone: UTC
        </Text>
      </Card>
    </Space>
  );
}
